import React, { useState, useEffect, useRef } from "react";
import {
  Lock,
  CreditCard,
  User,
  Mail,
  MapPin,
  Calendar,
  Shield,
  ChevronDown,
  ChevronUp,
  X,
  Loader2,
  PlayCircle,
  Check,
  Phone,
  Image,
  Video,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Toast from "../../../components/popups/toast";
import { DashboardRoute, PageNotFound, TermsOfServiceRoute, PrivacyPolicyRoute } from "../../../../../core/routes/route_constants";
import { usePaymentStateContext, PaymentUIAction, PaymentUIStatus, PaymentProcessStatus } from "../state/payment_state";
import { useDispatch } from "react-redux";
import PaymentLoadingComponent from "./payment_loading_component";
import PaymentSubscribeSuccessComponent from "./payment_subscribe_success_component";
import PaymentSubscribeOngoingComponent from "./payment_subscribe_ongoing_component";
import PaymentAddonSuccessComponent from "./payment_addon_success_component";
import PaymentAddonOngoingComponent from "./payment_addon_ongoing_component";
import { Eye, EyeOff, Clock, Play, Pause, VolumeX, Volume2, ArrowRight, Heart } from "lucide-react";
import TrackingService from "../../../../data/service/impl/tracking_service_impl";

const trackingService = new TrackingService();

const InputField = ({ icon, label, type, value, onChange, placeholder, id, error, fullWidth = false, autoComplete = "off", maxLength }) => (
  <div className={`mb-2 ${fullWidth ? "col-span-2" : ""}`}>
    <label htmlFor={id} className="block text-xs font-medium text-pink-300 mb-1 flex items-center">
      {icon && React.cloneElement(icon, { size: 14, className: "mr-2 text-pink-400" })}
      {label}
    </label>
    <div className="relative">
      <input
        type={type}
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        maxLength={maxLength}
        className={`w-full bg-slate-900/70 border ${error ? "border-red-600" : "border-red-900/30"
          } rounded-lg px-3 py-1.5 text-white placeholder-pink-300/50 focus:bg-slate-800/50 focus:ring-pink-600 focus:border-pink-600 outline-none transition-all shadow-sm`}
      />
    </div>
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

export default function PaymentComponent() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { transactionType, transactionId, from } = useParams();

  const {
    action,
    message,
    uiStatus,
    paymentInitialize,
    cardHolderFirstName,
    cardHolderFirstNameError,
    handleCardHolderFirstNameChange,
    cardHolderLastName,
    cardHolderLastNameError,
    handleCardHolderLastNameChange,
    cardNumber,
    cardNumberError,
    handleCardNumberChange,
    expiryDate,
    expiryDateError,
    handleExpiryDateChange,
    cvc,
    cvcError,
    handleCvcChange,
    showCvc,
    setShowCvc,
    showPaymentDetails,
    showBillingAddress,
    handleBillingAddressVisibilityChange,
    setAction,
    handlePaymentSubmit,
    billingAddressFirstName,
    billingAddressLastName,
    billingAddressEmail,
    billingAddressAddressLine1,
    billingAddressCity,
    billingAddressState,
    billingAddressZipCode,
    billingAddressCountry,
    billingAddressPhone,
    billingAddressFirstNameError,
    billingAddressLastNameError,
    billingAddressEmailError,
    billingAddressAddressLine1Error,
    billingAddressCityError,
    billingAddressStateError,
    billingAddressZipCodeError,
    billingAddressCountryError,
    billingAddressPhoneError,
    handleBillingAddressFirstNameChange,
    handleBillingAddressLastNameChange,
    handleBillingAddressEmailChange,
    handleBillingAddressAddressLine1Change,
    handleBillingAddressCityChange,
    handleBillingAddressStateChange,
    handleBillingAddressZipCodeChange,
    handleBillingAddressCountryChange,
    handleBillingAddressPhoneChange,
    paymentProcessStatus,
    addon,
  } = usePaymentStateContext();

  const [showVideoModal, setShowVideoModal] = useState(false);
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [selectedMode, setSelectedMode] = useState(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [consentGiven, setConsentGiven] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    paymentInitialize(transactionType, transactionId);
  }, [dispatch]);

  useEffect(() => {
    switch (action) {
      case PaymentUIAction.navigateToPageNotFound:
        navigate(PageNotFound);
        break;
      case PaymentUIAction.showErrorToast:
        Toast.error(message);
        break;
      case PaymentUIAction.showSuccessToast:
        Toast.success(message);
        break;
      default:
        break;
    }
    setAction(PaymentUIAction.initial);
  }, [action]);

  const handleSubmit = (e) => {
    e.preventDefault();
    handlePaymentSubmit();
  };

  const openVideoModal = () => setShowVideoModal(true);
  const closeVideoModal = () => setShowVideoModal(false);

  // Handle play/pause
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // Handle mute/unmute
  const toggleMute = (e) => {
    if (e) e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Update progress bar
  const updateProgress = () => {
    if (videoRef.current) {
      const value = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(value);
    }
  };

  // Hide controls after inactivity
  const resetControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    setShowControls(true);

    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const onClose = () => {
    if (from === "c") {
      window.location.href = `${DashboardRoute}/chat`;
    } else {
      window.location.href = `${DashboardRoute}/profile`;
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`h-screen w-screen bg-slate-950/70 backdrop-blur-md text-white relative`}>

      <div className="absolute top-0 left-0 bottom-0 right-0 overflow-y-auto flex flex-col items-center justify-center p-2 sm:p-4 lg:p-6">
        <div className="w-full max-w-4xl p-4 sm:p-6 lg:p-8 my-4 bg-slate-900/50 backdrop-blur-sm border border-red-900/50 rounded-xl shadow-2xl animate-fade-in">
          <header className="text-center mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-2xl font-bold text-pink-400 mb-1.5">
              {transactionType === "subscription" ? "Complete Your Premium Subscription" : "Complete Your Add-on Purchase"}
            </h1>
            <p className="text-pink-300 text-sm sm:text-base">
              {transactionType === "subscription"
                ? "Unlock the full ErotiX AI experience."
                : "Fuel your connection — unlock more hours for intimate video chats."}
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
            <div>
              {" "}
              {/* Wrapper div for Order Summary and Video Showcase */}
              {/* Combined Order Summary and Video Showcase Section */}
              <div className="bg-slate-900/80 border border-red-900/40 rounded-xl p-4 shadow-xl h-fit">
                
                {/* Order Summary Content */}
                <h2 className="text-xl font-semibold text-pink-200 mb-4 pb-2 border-b border-red-900/50">Order Summary</h2>

                {/* Billing Descriptor - Improved */}
                <div className="mb-4 flex flex-col items-center">
                  <div className="relative inline-flex items-center bg-gradient-to-r from-pink-700/80 to-red-800/80 border border-pink-400/30 rounded-xl px-4 py-2 shadow-md">
                    <span className="text-xs text-pink-100 font-semibold mr-2">
                      <span className="mr-1">For your privacy, your card statement will show:</span>
                      <span className="font-bold text-white bg-pink-600/80 px-2 py-0.5 rounded">
                        {transactionType === "subscription"
                          ? "EXAI TECHNOLOGIES*PREMIUM"
                          : addon && addon.name
                            ? `EXAI TECHNOLOGIES*${addon.name.toUpperCase().replace(/\s+/g, '-')}`
                            : "EXAI TECHNOLOGIES*ADDON"}
                      </span>
                    </span>
                    {/* Info icon with tooltip */}
                    <span className="ml-2 group relative cursor-pointer">
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="text-pink-200"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-black/90 text-xs text-white rounded px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-30 text-center shadow-lg">
                        This is how the charge will appear on your bank or card statement for your privacy and security.
                      </span>
                    </span>
                  </div>
                </div>

                {transactionType === "subscription" && (
                  <div className="mb-4 text-center">
                    {" "}
                    {/* Centering content below title */}
                    <span className="inline-block bg-slate-700/50 text-pink-300 text-xs font-medium px-2 py-1 rounded-full mb-2 tracking-wide">
                      ErotiX AI Premium
                    </span>
                    <div className="flex items-baseline justify-center mb-2">
                      {" "}
                      {/* Centering price, reduced bottom margin */}
                      <span className="text-4xl font-bold text-white mr-1.5">$9</span>
                      <span className="text-lg text-pink-300/80">/Monthly</span>
                    </div>
                  </div>
                )}

                {/* Premium Features List - Added Back */}
                {transactionType === "subscription" && (
                  <div className="mb-6 px-2">
                    <ul className="space-y-2 text-xs text-left">
                      {" "}
                      {/* text-left for list items */}
                      <li className="flex items-center text-pink-300">
                        <Check size={16} className="mr-2 text-pink-400 flex-shrink-0 p-0.5 bg-pink-500/20 rounded-md" />
                        Unlimited messaging
                      </li>
                      <li className="flex items-center text-pink-300">
                        <Phone size={16} className="mr-2 text-pink-400 flex-shrink-0 p-0.5 bg-pink-500/20 rounded-md" />
                        Unlimited phone calls
                      </li>
                      <li className="flex items-center text-pink-300">
                        <Image size={16} className="mr-2 text-pink-400 flex-shrink-0 p-0.5 bg-pink-500/20 rounded-md" />
                        Unlimited images
                      </li>
                      <li className="flex items-center text-pink-300">
                        <Video size={16} className="mr-2 text-pink-400 flex-shrink-0 p-0.5 bg-pink-500/20 rounded-md" />5 minutes teaser video chat
                      </li>
                    </ul>
                  </div>
                )}

                {transactionType === "addon" && addon && (
                  <div className="mb-4 text-center">
                    <span className="inline-block bg-gradient-to-r from-pink-600/90 to-red-600/90 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-2 tracking-wide shadow-[0_0_10px_rgba(236,72,153,0.2)] border-2 border-pink-400/40 hover:from-pink-500 hover:to-red-500 hover:shadow-[0_0_14px_rgba(236,72,153,0.3)] transition-all duration-300 transform hover:scale-105">
                      {addon.name}
                    </span>
                    <div className="flex items-baseline justify-center mb-2">
                      <span className="text-4xl font-bold text-white mr-1.5">${addon.price}</span>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2 mb-2">
                      <div className="flex items-center justify-center space-x-1 text-pink-300">
                        <Clock size={16} className="text-pink-400" />
                        <span className="text-lg font-bold">
                          {addon.value} {addon.unitOfTime}
                        </span>
                      </div>
                      <div className="flex flex-col items-center mt-1">
                        <p className="text-xs text-pink-300/80 mt-0.5">of premium connection time</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Video Showcase Content - Now a nested box */}
                <div className="relative aspect-video w-full flex items-center justify-center">
                  {/* Background image */}
                  <img
                    src="https://storage.googleapis.com/ai-companion-81b04.firebasestorage.app/avatar/eve/eve-highlight.png"
                    alt="Eve Highlight Background"
                    className="absolute inset-0 w-full h-full object-cover rounded-lg z-0"
                  />
                  {/* Black overlay */}
                  
                  {/* Content */}
                  <div
                    className="relative z-20 border border-pink-500/40 rounded-lg p-4 shadow-lg flex flex-col items-center justify-center w-full h-full cursor-pointer hover:bg-slate-700/70 transition-colors duration-300"
                    onClick={openVideoModal}
                  >
                    {/* <PlayCircle size={48} className="text-pink-400/70 mb-2" />
                    <h3 className="text-lg font-semibold text-pink-300 mb-1">Eve Video Showcase</h3>
                    <p className="text-xs text-pink-400/80 text-center">A glimpse into the future of connection.</p> */}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment and Billing Form Section */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Collapsible Payment Details Section */}
              <div
                className={`transition-all duration-500 ease-in-out overflow-hidden`}
                style={{ maxHeight: showPaymentDetails ? "1000px" : "0px", opacity: showPaymentDetails ? 1 : 0 }}
              >
                {showPaymentDetails && (
                  <div>
                    <h2 className="text-xl font-semibold text-pink-200 mb-4 pb-2 border-b border-red-900/50">Payment Details</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2">
                      <InputField
                        icon={<User />}
                        label="First Name"
                        type="text"
                        value={cardHolderFirstName}
                        onChange={(e) => handleCardHolderFirstNameChange(e.target.value)}
                        placeholder="First Name"
                        id="cardholderFirstName"
                        autoComplete="cc-given-name"
                        error={cardHolderFirstNameError}
                        maxLength={50}
                        fullWidth={screenWidth < 640}
                      />
                      <InputField
                        icon={<User />}
                        label="Last Name"
                        type="text"
                        value={cardHolderLastName}
                        onChange={(e) => handleCardHolderLastNameChange(e.target.value)}
                        placeholder="Last Name"
                        id="cardholderLastName"
                        autoComplete="cc-family-name"
                        error={cardHolderLastNameError}
                        maxLength={50}
                        fullWidth={screenWidth < 640}
                      />
                      <InputField
                        icon={<CreditCard />}
                        label="Card Number"
                        type="text"
                        value={cardNumber}
                        onChange={(e) => handleCardNumberChange(e.target.value)}
                        placeholder="•••• •••• •••• ••••"
                        id="cardNumber"
                        fullWidth={true}
                        autoComplete="cc-number"
                        maxLength={19}
                        error={cardNumberError}
                      />
                      <InputField
                        icon={<Calendar />}
                        label="Expiry Date"
                        type="text"
                        value={expiryDate}
                        onChange={(e) => handleExpiryDateChange(e.target.value, e.nativeEvent)}
                        placeholder="MM/YY"
                        id="expiryDate"
                        autoComplete="cc-exp"
                        maxLength={5}
                        error={expiryDateError}
                        fullWidth={screenWidth < 640}
                      />
                      <div className={`mb-2 ${screenWidth < 640 ? 'w-full' : ''}`}>
                        <label htmlFor="cvc" className="block text-xs font-medium text-pink-300 mb-1 flex items-center">
                          <Lock size={14} className="mr-2 text-pink-400" />
                          CVC
                        </label>
                        <div className="relative">
                          <input
                            type={showCvc ? "text" : "password"}
                            id="cvc"
                            value={cvc}
                            onChange={(e) => handleCvcChange(e.target.value)}
                            placeholder="••••"
                            autoComplete="cc-csc"
                            maxLength={4}
                            className={`w-full bg-slate-900/70 border ${cvcError ? "border-red-600" : "border-red-900/30"
                              } rounded-lg px-3 py-1.5 text-white placeholder-pink-300/50 focus:bg-slate-800/50 focus:ring-pink-600 focus:border-pink-600 outline-none transition-all shadow-sm pr-10`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowCvc(!showCvc)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-pink-400 hover:text-pink-300 transition-colors"
                          >
                            {showCvc ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        {cvcError && <p className="text-red-500 text-xs mt-1">{cvcError}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Clickable Billing Address Header */}
              <div
                className={`p-2.5 rounded-lg bg-slate-900/50 hover:bg-slate-800/60 border border-red-900/30 cursor-pointer transition-all flex justify-between items-center ${showPaymentDetails ? "mt-8" : "mt-0"
                  } mb-2`}
                onClick={handleBillingAddressVisibilityChange}
              >
                <h2 className="text-base font-semibold text-pink-200">Billing Address</h2>
                {showBillingAddress ? <ChevronUp size={22} className="text-pink-400" /> : <ChevronDown size={22} className="text-pink-400" />}
              </div>

              {/* Collapsible Billing Address Form */}
              <div
                className={`transition-all duration-500 ease-in-out overflow-hidden ${showBillingAddress ? "mt-0" : "-mt-4"}`}
                style={{ maxHeight: showBillingAddress ? "1000px" : "0px", opacity: showBillingAddress ? 1 : 0 }}
              >
                {showBillingAddress && (
                  <div className="pt-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                      <InputField
                        icon={<User />}
                        label="First Name"
                        type="text"
                        value={billingAddressFirstName}
                        onChange={(e) => handleBillingAddressFirstNameChange(e.target.value)}
                        placeholder="First Name"
                        id="billingFirstName"
                        autoComplete="given-name"
                        error={billingAddressFirstNameError}
                        maxLength={50}
                        fullWidth={screenWidth < 640}
                      />
                      <InputField
                        icon={<User />}
                        label="Last Name"
                        type="text"
                        value={billingAddressLastName}
                        onChange={(e) => handleBillingAddressLastNameChange(e.target.value)}
                        placeholder="Last Name"
                        id="billingLastName"
                        autoComplete="family-name"
                        error={billingAddressLastNameError}
                        maxLength={50}
                        fullWidth={screenWidth < 640}
                      />
                      <InputField
                        icon={<Mail />}
                        label="Email Address"
                        type="email"
                        value={billingAddressEmail}
                        onChange={(e) => handleBillingAddressEmailChange(e.target.value)}
                        placeholder="you@example.com"
                        id="billingEmail"
                        autoComplete="email"
                        fullWidth={true}
                        error={billingAddressEmailError}
                      />
                      <InputField
                        label="Phone"
                        type="text"
                        value={billingAddressPhone}
                        onChange={(e) => handleBillingAddressPhoneChange(e.target.value)}
                        placeholder="123-456-7890"
                        id="phone"
                        autoComplete="tel"
                        error={billingAddressPhoneError}
                        fullWidth={true}
                      />
                      <InputField
                        icon={<MapPin />}
                        label="Address Line 1"
                        type="text"
                        value={billingAddressAddressLine1}
                        onChange={(e) => handleBillingAddressAddressLine1Change(e.target.value)}
                        placeholder="123 Fantasy Lane"
                        id="addressLine1"
                        fullWidth={true}
                        autoComplete="address-line1"
                        error={billingAddressAddressLine1Error}
                      />
                      <InputField
                        label="City"
                        type="text"
                        value={billingAddressCity}
                        onChange={(e) => handleBillingAddressCityChange(e.target.value)}
                        placeholder="Dreamville"
                        id="city"
                        autoComplete="address-level2"
                        error={billingAddressCityError}
                        fullWidth={screenWidth < 640}
                      />
                      <InputField
                        label="State / Province"
                        type="text"
                        value={billingAddressState}
                        onChange={(e) => handleBillingAddressStateChange(e.target.value)}
                        placeholder="CA"
                        id="state"
                        autoComplete="address-level1"
                        error={billingAddressStateError}
                        fullWidth={screenWidth < 640}
                      />
                      <InputField
                        label="ZIP / Postal Code"
                        type="text"
                        value={billingAddressZipCode}
                        onChange={(e) => handleBillingAddressZipCodeChange(e.target.value)}
                        placeholder="90210"
                        id="zipCode"
                        autoComplete="postal-code"
                        error={billingAddressZipCodeError}
                        fullWidth={screenWidth < 640}
                      />
                      <InputField
                        label="Country"
                        type="text"
                        value={billingAddressCountry}
                        onChange={(e) => handleBillingAddressCountryChange(e.target.value)}
                        placeholder="United States"
                        id="country"
                        autoComplete="country-name"
                        error={billingAddressCountryError}
                        fullWidth={screenWidth < 640}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Subscribe Button - Now always visible if form is conceptually active */}
              <button
                type="submit"
                className="w-full mt-4 py-2.5 px-4 bg-gradient-to-r from-red-700 via-red-800 to-red-900 hover:from-red-800 hover:to-red-950 rounded-lg text-white font-semibold text-base shadow-lg hover:shadow-red-700/40 transition-all duration-300 transform hover:scale-105 active:translate-y-0.5 active:shadow-none flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={paymentProcessStatus === PaymentProcessStatus.processing}
              >
                {paymentProcessStatus === PaymentProcessStatus.processing ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock size={18} className="mr-2" />
                    {transactionType === "subscription" ? "Subscribe & Pay $9" : `Pay for $${addon?.price}`}
                  </>
                )}
              </button>
              {/* Legal text and New Security Message */}
              {(showPaymentDetails || showBillingAddress) && (
                <div className="mt-2 text-center">
                  <p className="text-xs text-pink-300/75">
                    By clicking {transactionType === "subscription" ? "Subscribe & Pay" : "Pay"}, you agree to our
                    <a className="text-pink-400 hover:underline cursor-pointer mx-1" onClick={() => navigate(TermsOfServiceRoute)}>Terms of Service</a>
                    and
                    <span className="text-pink-400 hover:underline cursor-pointer mx-1" onClick={() => navigate(PrivacyPolicyRoute)}>Privacy Policy.</span>
                    {transactionType === "subscription" ? " Your subscription will auto-renew monthly." : " This purchase is a one-time add-on, and your card will be charged accordingly."}
                  </p>
                  <div className="flex items-center justify-center mt-2 text-pink-300/75 text-xs">
                    <Shield size={12} className="mr-1 flex-shrink-0 text-pink-300/75" />
                    <span>Secure SSL Encrypted Payment.</span>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white-300 hover:text-white hover:bg-slate-800/70 p-2 rounded-full transition-colors"
        aria-label="Close checkout"
      >
        <X size={24} />
      </button>

      {/* Video Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
          tabIndex={-1}
          onKeyDown={e => { if (e.key === 'Escape') closeVideoModal(); }}
        >
          <div className="relative w-full max-w-md mx-2 pt-4 pb-2 bg-slate-900/80 border border-pink-600 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-lg transition-transform duration-300 scale-100 animate-scale-in flex flex-col items-center">
            {/* Close Button */}
            <button
              onClick={closeVideoModal}
              className="absolute top-2 right-2 z-10 text-pink-300 hover:text-white hover:bg-pink-500/20 p-1.5 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-400"
              aria-label="Close video modal"
            >
              <X size={20} />
            </button>
            {/* Header */}
            <div className="w-full text-center pt-2 pb-1 px-2">
              <h2 className="text-xl font-extrabold text-pink-400 tracking-tight">Eve Video Preview</h2>
              <div className="w-12 h-1 mx-auto mt-1 mb-2 bg-gradient-to-r from-pink-400/60 to-transparent rounded-full" />
            </div>
            {/* Video Area */}
            <div className="w-full px-2">
              <div className="relative w-full aspect-[16/9] max-h-[40vh] flex items-center justify-center">
                {/* Blurred thumbnail as background */}
                <img
                  src="https://storage.googleapis.com/ai-companion-81b04.firebasestorage.app/avatar/eve/eve-highlight.png"
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover blur-lg scale-110 opacity-40 z-0 pointer-events-none select-none"
                  draggable="false"
                />
                {/* Video with border glow */}
                <div className="relative z-10 w-full h-full rounded-xl overflow-hidden border-2 border-pink-400/30 shadow-lg flex items-center justify-center">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover rounded-xl bg-black"
                    autoPlay
                    loop
                    playsInline
                    controls={false}
                    disablePictureInPicture
                    disableRemotePlayback
                    muted={isMuted}
                    onTimeUpdate={updateProgress}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                    onClick={togglePlay}
                    preload="auto"
                    poster="https://storage.googleapis.com/ai-companion-81b04.firebasestorage.app/avatar/eve/eve-highlight.png"
                  >
                    <source src="https://firebasestorage.googleapis.com/v0/b/ai-companion-81b04.firebasestorage.app/o/avatar%2Feve%2Feve_speaking.mp4?alt=media&token=c020924c-5043-4273-ab56-0c3353277f8b" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
            {/* Controls */}
            <div className={`w-full px-2 pb-0 pt-1 bg-transparent ${showControls ? '' : 'hidden'}`}>
              {/* Progress bar */}
              <div className="relative w-full h-2 bg-gray-700/50 rounded-full mb-2 cursor-pointer group"
                onClick={e => {
                  if (videoRef.current) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const position = (e.clientX - rect.left) / rect.width;
                    videoRef.current.currentTime = position * videoRef.current.duration;
                  }
                }}>
                <div className="absolute h-2 bg-pink-400 rounded-full" style={{ width: `${progress}%` }} />
                <div className="absolute top-0 h-2 w-2 bg-pink-400 rounded-full shadow-lg" style={{ left: `calc(${progress}% - 4px)` }} />
              </div>
              {/* Control buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    className="w-10 h-10 rounded-full bg-red-900/30 flex items-center justify-center hover:bg-pink-500/30 transition-colors"
                    onClick={e => { e.stopPropagation(); togglePlay(); }}
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? <Pause size={18} className="text-white" /> : <Play size={18} className="text-white" />}
                  </button>
                  <button
                    className="w-10 h-10 rounded-full bg-red-900/30 flex items-center justify-center hover:bg-pink-500/30 transition-colors"
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? <VolumeX size={18} className="text-white" /> : <Volume2 size={18} className="text-white" />}
                  </button>
                  <div className="text-sm text-pink-300 font-mono min-w-[80px] text-center">
                    {videoRef.current ? (
                      <>
                        {Math.floor(videoRef.current.currentTime / 60)}:
                        {String(Math.floor(videoRef.current.currentTime % 60)).padStart(2, '0')} /
                        {Math.floor((videoRef.current.duration || 0) / 60)}:
                        {String(Math.floor((videoRef.current.duration || 0) % 60)).padStart(2, '0')}
                      </>
                    ) : (
                      '0:00 / 0:00'
                    )}
                  </div>
                </div>
                {/* HD badge with tooltip */}
                <div className="relative group">
                  <span className="bg-pink-600/80 px-2 py-0.5 rounded text-xs font-semibold text-white flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="10" rx="2" /><path d="M7 10v4M7 12h2m6-2v4m0-2h2" /></svg>
                    HD
                  </span>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black/80 text-xs text-white rounded opacity-0 group-hover:opacity-100 transition">High Definition</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {paymentProcessStatus === PaymentProcessStatus.success && transactionType === "subscription" && <PaymentSubscribeSuccessComponent />}
      {paymentProcessStatus === PaymentProcessStatus.success && transactionType === "addon" && <PaymentAddonSuccessComponent minutes={addon?.value ?? "0"} />}

      {paymentProcessStatus === PaymentProcessStatus.processing && transactionType === "subscription" && <PaymentSubscribeOngoingComponent />}
      {paymentProcessStatus === PaymentProcessStatus.processing && transactionType === "addon" && <PaymentAddonOngoingComponent />}
    </div>
  );
}
